/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css, SerializedStyles } from '@emotion/react/macro'
import { ReactElement } from 'react'
import { useLocation } from 'react-router-dom'

import IconButton from '../../buttons/IconButton'
import useMixpanelEvent from '../../../../lib/hooks/useMixpanelEvent'
import useTheme from '../../../../lib/hooks/useTheme'
import { getFontColor, getFocusColor } from '../../../../lib/colorUtils'
import { userIsAdmin } from '../../../../lib/authenticationUtils'
import { Auth0User } from '../../../../types'
import { Target } from '../../buttons/Button'

type NavBarButtonProps = {
  /** Accessibility label to be applied to the button */
  ariaLabel: string
  /** Optional CSS overwrite for the button */
  buttonCss?: SerializedStyles
  /** Icon element for the button */
  icon: ReactElement
  /** Whether this is a redirect outside of the student app.
   * Also needs the route prop to redirect properly.
   */
  isExternalLink?: boolean
  /** Whether the NavBar parent is expanded */
  isNavExpanded: boolean
  /** Whether the NavBar parent is persisted */
  isNavPersisted: boolean
  /** Label to apply when the NavBar is expanded */
  label: string
  /** Optional CSS overwrite for the label */
  labelCss?: SerializedStyles
  /** Optional click handler */
  onClick?: () => void
  /** Function to shrink the NavBar */
  onShrink?: () => void
  /** The route that is represented by this button
   * Note: Some nav buttons might not represent routes */
  route?: string
  /** Paired with isExternalLink. Specifies where to open the linked document. */
  target?: Target
  /** If we should add a mixpanel event onClick of the button */
  trackNavigation?: boolean
  /** User object from useAuth0 */
  user?: Auth0User | undefined
}

const defaultProps: Omit<
  NavBarButtonProps,
  'ariaLabel' | 'icon' | 'isNavExpanded' | 'isNavPersisted' | 'label'
> = {
  buttonCss: undefined,
  isExternalLink: false,
  labelCss: undefined,
  onClick: undefined,
  onShrink: undefined,
  route: undefined,
  target: undefined,
  trackNavigation: false,
  user: undefined
}

/** IMPORTANT:  This code is duplicated in scheduler.
 * Verify any changes here are also made in scheduler.
 */
const NavBarButton = ({
  ariaLabel,
  buttonCss,
  onShrink,
  icon,
  isNavExpanded,
  isNavPersisted,
  isExternalLink,
  label,
  labelCss,
  onClick,
  route,
  target,
  trackNavigation,
  user
}: NavBarButtonProps) => {
  const { navbarColor } = useTheme()
  const fontColor = getFontColor(navbarColor!)
  const { addMixpanelEvent } = useMixpanelEvent()

  const handleClick = () => {
    if (onShrink && !isNavPersisted) {
      onShrink()
    }

    if (trackNavigation) {
      addMixpanelEvent(`Navigate to ${label}`)
    }

    if (onClick) {
      onClick()
    }
  }

  const labelStyle =
    labelCss ||
    css`
      color: ${fontColor};
      display: inline-block;
      padding-left: 15px;
      line-height: 26px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `
  const { pathname } = useLocation()
  const isAdmin = userIsAdmin(user)
  const isActive =
    (route && pathname === route) ||
    (!isAdmin && route === '/plans' && pathname === '/') ||
    (isAdmin && route === '/dashboard' && pathname === '/')
  const buttonStyle =
    buttonCss ||
    css`
      ${isNavExpanded && 'justify-content: start;'}
      border-radius: 4px;
      align-content: center;
      color: ${fontColor};
      display: flex;
      font-size: 1rem;
      grid-column: 1;
      height: 36px;
      justify-content: ${isNavExpanded ? 'start' : 'center'};
      opacity: ${isActive ? '100%' : '80%'};
      text-transform: none;
      transition: all 0.3s ease;
      width: ${isNavExpanded ? '168px' : '16px'};

      :hover,
      :focus {
        background: ${getFocusColor(navbarColor!, undefined, true)};
        color: ${fontColor};
        cursor: pointer;
      }

      svg {
        vertical-align: top;
      }

      a {
        padding: 0px 16px;
        text-align: center;
      }
    `

  const nonRouteButtonContainerCss = css`
    color: ${fontColor};
    display: flex;
    align-content: center;
    justify-content: ${isNavExpanded ? 'start' : 'center'};

    button {
      height: 48px;
      width: ${isNavExpanded ? '230px' : '52px'};
      padding: 12px 16px;
    }
  `

  return route ? (
    <IconButton
      ariaLabel={ariaLabel}
      color={navbarColor!}
      css={buttonStyle}
      displayTooltip={false}
      exact={route === '/'}
      href={route}
      isActive={isActive}
      isExternalLink={isExternalLink}
      isNav
      onClick={handleClick}
      target={target}
    >
      {icon}
      {isNavExpanded && <div css={labelStyle}>{label}</div>}
    </IconButton>
  ) : (
    <div css={nonRouteButtonContainerCss}>
      <IconButton
        ariaLabel={ariaLabel}
        color={navbarColor!}
        css={buttonStyle}
        displayTooltip={false}
        onClick={handleClick}
      >
        {icon}
        {isNavExpanded && <div css={labelStyle}>{label}</div>}
      </IconButton>
    </div>
  )
}

NavBarButton.defaultProps = defaultProps

export default NavBarButton
